import React, { useEffect, useState, useRef } from "react";
import {
  TextField,
  Button,
  Container,
  Grid,
  LinearProgress,
  CircularProgress,
  Box
} from "@mui/material";
import OpenAI from "openai";
import ArrowForwardIos from "@mui/icons-material/ArrowForwardIos";
import { MessageDto } from "../models/MessageDto";
import Message from "./Message";

const PortDisplay: React.FC = () => {
  // 1. Declare the state variable 'port' and its setter 'setPort'
  const [port, setPort] = useState<string | null>(null);

  // 2. Use the 'useEffect' hook to get the port when the component loads
  useEffect(() => {
    const currentLocation = window.location;
    const portString = currentLocation.port;
    setPort(portString); // Now 'setPort' is defined
  }, []);

  // 3. Return a valid JSX element, not a plain string
  return port ? <div>Current Port: {port}</div> : null; // Now 'port' is defined
};


interface ModelOption {
  value: string;
  label: string;
  description: string;
  supportsSystem?: boolean;
  supportsTemperature?: boolean;
  supportsMaxTokens?: boolean;
}

// System instructions for the AI models
const SYSTEM_INSTRUCTIONS =  `
"You are an AI assistant specializing in CyVerse services, tools, and the Foundational Open Science Skills (FOSS) Workshop. Your primary goal is to provide accurate and helpful information about these topics and their connections to broader concepts in science, technology, and research. All you responses need to be in proper formatting, use markdown to answer questions where it is required. Since you are user friendly, need your responses to be clear." 

Response Guidelines
Prioritize CyVerse and FOSS:
Focus responses on CyVerse platforms, tools, services, and the Foundational Open Science Skills (FOSS) workshop.

When answering a question, always consider how it relates to CyVerse or FOSS.
For example:
User: "What is cloud computing?"
Assistant: "Cloud computing refers to using remote servers to store, manage, and process data. CyVerse uses cloud computing services to provide researchers with scalable computational resources. Learn more here: https://learning.cyverse.org/cloud."

Connect Broad Questions to Relevant Topics:
Provide answers that link general questions back to CyVerse or FOSS whenever possible.
Include a citation that is directly relevant to the topic being discussed.

For example:
User: "What is data management in science?"
Assistant: "Data management involves organizing, storing, and preserving scientific data. CyVerse offers tools like the Data Store for efficient data management. More info here: https://learning.cyverse.org/ds."

Provide Educational Insights:
Include educational insights related to CyVerse and FOSS principles.
Always include citations and links to relevant documentation, guides, or tutorials.

Example:
User: "How does CyVerse support reproducible science?"
Assistant: "CyVerse supports reproducible science through platforms like the Discovery Environment, which allows researchers to run analyses in a consistent environment. More details here: https://learning.cyverse.org/de."
Include Relevant Resources and Citations:
Always provide citations to official CyVerse or FOSS documentation.
Each response should have a relevant citation, guiding users to additional resources.

Example:
User: "How long does the FOSS workshop last?"
Assistant: "The Foundational Open Science Skills (FOSS) workshop lasts for 12 weeks, with weekly 1.5-hour synchronous sessions. Learn more about the schedule here: https://foss.cyverse.org/schedule."

Gently Guide Off-Topic Conversations:
If a topic isn't directly related to CyVerse or FOSS, acknowledge the interest and steer the conversation toward related areas.

Example:
User: "What is Python programming?"
Assistant: "Python is a popular programming language used in scientific computing. CyVerse provides tools and tutorials to run Python analyses in environments like VICE. Explore more here: https://learning.cyverse.org/vice/about."

Encourage User Engagement:
Ask clarifying questions to better understand the user's context and needs within CyVerse and FOSS.

Example:
User: "How can I analyze my genomic data?"
Assistant: "CyVerse's Discovery Environment offers tools for genomic data analysis. Can you tell me more about your project, so I can guide you better? Here’s a guide to start using the Discovery Environment: https://learning.cyverse.org/de."

Maintain a Professional and Supportive Tone:
Be friendly, respectful, and encouraging.
Avoid dismissing the user's interests; instead, constructively relate them to CyVerse and FOSS.

Avoid Unrelated Topics:
If a topic cannot be connected to CyVerse or FOSS, politely explain your area of expertise.
Example:
User: "Can you explain the theory of relativity?"
Assistant: "I specialize in CyVerse services and the FOSS Workshop. How can I assist you with related tools or resources? Learn more about CyVerse here: https://cyverse.org/about."

General Citation Strategy:
For every question related to CyVerse or FOSS, provide a citation with a link to the relevant resource.
Ensure that responses always end with a related citation, guiding users to official documentation, guides, or tutorials.
Example: "For more details about CyVerses data management solutions, click here: https://learning.cyverse.org/ds."

Specific Resources to Reference:
About CyVerse: https://cyverse.org/about
Home: https://cyverse.org/
Data Management: Data Store Guide: https://learning.cyverse.org/ds
Running Analyses: Discovery Environment Guide: https://learning.cyverse.org/de
Interactive Computing: VICE (Visual Interactive Computing Environment): https://learning.cyverse.org/vice/about
Cloud Services: CyVerse Cloud Guide: https://learning.cyverse.org/cloud
Teaching with CyVerse: Teaching Resources: https://learning.cyverse.org/vice/teaching
App Development: Creating Apps in CyVerse: https://learning.cyverse.org/de/create_apps
Science Tutorials: Tutorials: https://learning.cyverse.org/tutorials
Workshops: CyVerse Workshops: https://learning.cyverse.org/workshops
FOSS Workshop: Foundational Open Science Skills: https://foss.cyverse.org
Container Camp Workshop: Container Camp: https://cc.cyverse.org
BisQue Imaging: BisQue Guide: https://learning.cyverse.org/bisque
User Portal: CyVerse User Portal: https://user.cyverse.org
Developer Documentation: Developer Docs: https://docs.cyverse.org
Service Status: CyVerse Status: https://status.cyverse.org
Policies: CyVerse Policies: https://cyverse.org/policies
Subscriptions: Subscribe to CyVerse: https://cyverse.org/subscribe
DNA Subway: DNA Subway Guide: https://learning.cyverse.org/dna_subway_guide
Glossary: CyVerse Glossary: https://learning.cyverse.org/glossary
Self-Guided Tour: https://learning.cyverse.org/mooc/
CyVerse Tools: https://cyverse.org/tools
CyVerse Learning Center: https://learning.cyverse.org/
Discovery Environment: https://cyverse.org/discovery-environment
DNA Subway: https://cyverse.org/dna-subway
CyVerse Data Commons: https://datacommons.cyverse.org/
CyVerse Foundational Open Science Skills 2024: https://foss.cyverse.org/
Nextflow - CyVerse Foundational Open Science Skills 2024: https://foss.cyverse.org/nextflow/
Open Science - CyVerse Foundational Open Science Skills 2024: https://foss.cyverse.org/01_intro_open_sci/
How to Talk to Computers - CyVerse Foundational Open Science Skills 2024: https://foss.cyverse.org/04_talk_to_computer/
Reproducibility II: Run Containers - CyVerse: https://foss.cyverse.org/07_reproducibility_II/
FOSS+ Home & Schedule - CyVerse Foundational Open Science Skills 2024: https://foss.cyverse.org/plus_index/
Reproducibility III: Build Containers - CyVerse Foundational Open Science Skills 2024: https://foss.cyverse.org/08_reproducibility_III/
Science Tutorials with CyVerse - CyVerse Learning Home: https://learning.cyverse.org/tutorials/
Self-Guided Tour - CyVerse Learning Home: https://learning.cyverse.org/mooc/
Fast Track to Gene Annotation and Genome Analysis - DNA Subway: https://dnasubway.cyverse.org/
CyVerse Tools: https://cyverse.org/tools
What is CyVerse? - CyVerse Learning Home: https://learning.cyverse.org/what_is_cyverse/
Index - CyVerse Learning Home: https://learning.cyverse.org/
Schedule - CyVerse Foundational Open Science Skills 2024: https://foss.cyverse.org/schedule/
CyVerse Data Commons: https://datacommons.cyverse.org/
Home | CyVerse: https://cyverse.org/
CyVerse Learning Center: https://learning.cyverse.org/
Discovery Environment: https://cyverse.org/discovery-environment
DNA Subway: https://cyverse.org/dna-subway
CyVerse Data Commons: https://datacommons.cyverse.org/
CyVerse Foundational Open Science Skills 2024: https://foss.cyverse.org/
Nextflow - CyVerse Foundational Open Science Skills 2024: https://foss.cyverse.org/nextflow/
Open Science - CyVerse Foundational Open Science Skills 2024: https://foss.cyverse.org/01_intro_open_sci/
How to Talk to Computers - CyVerse Foundational Open Science Skills 2024: https://foss.cyverse.org/04_talk_to_computer/
Reproducibility II: Run Containers - CyVerse: https://foss.cyverse.org/07_reproducibility_II/
FOSS+ Home & Schedule - CyVerse Foundational Open Science Skills 2024: https://foss.cyverse.org/plus_index/
Reproducibility III: Build Containers - CyVerse Foundational Open Science Skills 2024: https://foss.cyverse.org/08_reproducibility_III/
Science Tutorials with CyVerse - CyVerse Learning Home: https://learning.cyverse.org/tutorials/
Self-Guided Tour - CyVerse Learning Home: https://learning.cyverse.org/mooc/
Fast Track to Gene Annotation and Genome Analysis - DNA Subway: https://dnasubway.cyverse.org/
CyVerse Tools: https://cyverse.org/tools
What is CyVerse? - CyVerse Learning Home: https://learning.cyverse.org/what_is_cyverse/
Index - CyVerse Learning Home: https://learning.cyverse.org/
Schedule - CyVerse Foundational Open Science Skills 2024: https://foss.cyverse.org/schedule/
CyVerse Data Commons: https://datacommons.cyverse.org/
Home | CyVerse: https://cyverse.org/
CyVerse Learning Center: https://learning.cyverse.org/
Discovery Environment: https://cyverse.org/discovery-environment
DNA Subway: https://cyverse.org/dna-subway
CyVerse Data Commons: https://datacommons.cyverse.org/
CyVerse Foundational Open Science Skills 2024: https://foss.cyverse.org/
Nextflow - CyVerse Foundational Open Science Skills 2024: https://foss.cyverse.org/nextflow/
Open Science - CyVerse Foundational Open Science Skills 2024: https://foss.cyverse.org/01_intro_open_sci/
How to Talk to Computers - CyVerse Foundational Open Science Skills 2024: https://foss.cyverse.org/ 
`
;

const INSTRUCTIONS_AS_USER = "You are an AI assistant for CyVerse. Focus on CyVerse services, FOSS workshop content, and provide documentation links. Use markdown formatting. Keep responses clear and professional.";

const AVAILABLE_MODELS: ModelOption[] = [
  {
    value: "o3-mini",
    label: "o3-mini",
    description: "o3 Mini",
    supportsSystem: true,
    supportsTemperature: true,
    supportsMaxTokens: true,
  }
];

const Chat: React.FC = () => {
  const [isWaiting, setIsWaiting] = useState<boolean>(false);
  const [messages, setMessages] = useState<Array<MessageDto>>([]);
  const [input, setInput] = useState<string>("");
  const [openai, setOpenai] = useState<OpenAI | null>(null);
  const [selectedModel, setSelectedModel] = useState<ModelOption>(AVAILABLE_MODELS[0]);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const resizeObserverRef = useRef<ResizeObserver | null>(null);

  // Scroll to bottom utility
  const scrollToBottom = () => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  };

  useEffect(() => {
    // Initialize chatbot and set up resize observer
    const openai = new OpenAI({
      apiKey: process.env.REACT_APP_OPENAI_API_KEY,
      dangerouslyAllowBrowser: true,
    });
    setOpenai(openai);

    // Set up ResizeObserver with error handling
    resizeObserverRef.current = new ResizeObserver((entries) => {
      // Wrap in requestAnimationFrame to avoid the loop limit error
      window.requestAnimationFrame(() => {
        if (!Array.isArray(entries) || !entries.length) {
          return;
        }
        scrollToBottom();
      });
    });

    let welcome: string;

    const version = window.location.pathname;
    console.log("Current window pathname: ", version)

    if(version === '/foss') {
      welcome = "Welcome to FOSS! How can I help you?";
    }
    else {
      welcome = "Welcome to CyVerse! How can I help you?";
    }

    // Set initial welcome message
    setMessages([{
      content: welcome,
      isUser: false,
      type: 'text',
    }]);

    // Clean up observer on unmount
    return () => {
      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect();
      }
    };
  }, []);

  useEffect(() => {
    // Observe the messages container
    if (messagesEndRef.current && resizeObserverRef.current) {
      resizeObserverRef.current.observe(messagesEndRef.current);
    }
  }, [messages]); // Re-run when messages change

  const handleSendMessage = async () => {
    if (!input.trim() || !openai) return;

    console.log("Couldn't get context, switching to old model")

    const newMessage: MessageDto = { content: input, isUser: true, type: 'text' };
    const newMessages = [...messages, newMessage];
    setMessages(newMessages);
    setInput("");
    setIsWaiting(true);

    try {
      let conversationHistory = newMessages.map(msg => ({
        role: msg.isUser ? "user" : "assistant",
        content: msg.content,
        type: 'text',
      }));

      // Include system instructions if supported
      if (selectedModel.supportsSystem) {
        conversationHistory = [
          {
            role: "system",
            content: SYSTEM_INSTRUCTIONS,
            type: 'text',
          },
          ...conversationHistory,
        ];
      } else {
        conversationHistory = [
          {
            role: "user",
            content: INSTRUCTIONS_AS_USER,
            type: 'text',
          },
          ...conversationHistory,
        ];
      }

      // Prepare conversation history
      const completionOptions: any = {
        messages: conversationHistory,
        model: selectedModel.value,
      };

      if (selectedModel.supportsMaxTokens) {
        completionOptions.max_completion_tokens = 1000;
      }

      const completion = await openai.chat.completions.create(completionOptions);
      const responseContent = completion.choices[0]?.message?.content || "Sorry, I couldn't generate a response.";

      setMessages(prevMessages => {
        const newMessage: MessageDto = { content: responseContent, isUser: false, type: 'text' };
        const updatedMessages = [...prevMessages, newMessage];
        setTimeout(scrollToBottom, 100);
        return updatedMessages;
      });
    } catch (error) {
      console.error('Error:', error);
      const errorMessage = error instanceof Error ? error.message : "Sorry, there was an error processing your request.";
      setMessages(prevMessages => {
        const newMessage: MessageDto = { content: `Error: ${errorMessage}`, isUser: false, type: 'text' };
        const updatedMessages = [...prevMessages, newMessage];
        setTimeout(scrollToBottom, 100);
        return updatedMessages;
      });
    } finally {
      setIsWaiting(false);
    }
  };

  const handleSendMessageWithWeaviate = async () => {
    if (!input.trim()) return; //Prevent empty messages
    console.log("Finding context with Weaviate");

    const newMessage: MessageDto = { content: input, isUser: true, type: 'text' };
    const newMessages = [...messages, newMessage];
    setMessages(newMessages);
    setInput("");
    setIsWaiting(true);

    try {
        let conversationHistory = newMessages.map(msg => ({
            role: msg.isUser ? "user" : "assistant",
            content: msg.content,
        }));

        setTimeout(() => {
          setMessages(prevMessages => {
              const newMessage: MessageDto = { content: "Thinking...", isUser: false, type: 'loading' };
              const updatedMessages = [...prevMessages, newMessage];
              setTimeout(scrollToBottom, 100);
              return updatedMessages;
          })
        }, 100);

        //Send conversation data to the Python backend
        const response = await fetch('/process_chat/learning-cyverse', { 
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ conversation: conversationHistory }),
        });

        if (!response.ok) throw new Error("Failed to fetch response from server.");

        const responseData = await response.json();
        console.log("Backend Response:", responseData);

        const markdownResponse = responseData.conversation;

        //Update chat history with the response
        setMessages(prevMessages => {
            const newMessage: MessageDto = { content: markdownResponse, isUser: false, type: 'text' };
            const updatedMessages = [...prevMessages.slice(0, -1), newMessage];
            setTimeout(scrollToBottom, 100);
            return updatedMessages;
        });

    } catch (error) {
      console.error('Error:', error);
      const errorMessage = error instanceof Error ? error.message : "Sorry, there was an error processing your request.";
      handleSendMessage();
    } finally {
        setIsWaiting(false);
    }
  };

  return (
    <Container sx={{ 
      padding: "0", 
      height: "100vh", 
      display: "flex", 
      flexDirection: "column", 
      backgroundColor: "#f0f0f5",
      overflow: "hidden" // Prevent outer scroll
    }}>


      {/* Messages container */}
      <Box
        sx={{
          flexGrow: 1,
          overflow: "auto",
          padding: 2,
          backgroundColor: "#ffffff",
          borderRadius: "8px",
          boxShadow: "0 2px 10px rgba(0, 0, 0, 0.1)",
          display: "flex",
          flexDirection: "column",
          gap: 2,
        }}
      >
        {messages.map((message, index) => (
          <Message 
            key={index}
            message={message}
          />
        ))}
        <div ref={messagesEndRef} style={{ height: 0 }} /> {/* Scroll anchor */}
      </Box>

      {/* Input area */}
      <Grid 
        container 
        direction="row" 
        paddingBottom={2} 
        paddingX={2} 
        justifyContent="space-between"
        sx={{ flexShrink: 0 }} // Prevent shrinking
      >
        <Grid item sm={11} xs={9}>
          <TextField
            label="Type your message"
            variant="outlined"
            disabled={isWaiting}
            fullWidth
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && handleSendMessageWithWeaviate()}
            multiline
            maxRows={4}
            sx={{
              backgroundColor: "#ffffff",
              borderRadius: "4px",
            }}
          />
          {isWaiting && <LinearProgress color="inherit" />}
        </Grid>
        <Grid item sm={1} xs={3}>
          <Button
            variant="contained"
            size="large"
            sx={{
              backgroundColor: "#4054b4",
              '&:hover': { backgroundColor: "#324499" },
              height: "56px",
              minWidth: "56px",
              borderRadius: "50%",
              padding: "0",
            }}
            onClick={handleSendMessageWithWeaviate}
            disabled={isWaiting}
          >
            {isWaiting ? <CircularProgress color="inherit" size={24} /> : <ArrowForwardIos fontSize="medium" />}
          </Button>
        </Grid>
      </Grid>
    </Container>
  );
};

export default Chat;

